import { FC } from 'react';
import { Nullable } from '@common/type/CommonTypes';
import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import BaseWarningModal from '@components/share/modal/baseWarningModal';
import { Box, Button, DialogActions, Link, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';

type CreditWarningEnrollCourseGroupDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  degreeCourseGroups: {
    productNames: string[];
    degreeName: string;
    majorName: string;
    facultyName: string;
    urlPdfCourseStructureAndCondition: Nullable<string>;
  }[];
};
const CreditWarningEnrollCourseGroupDialog: FC<CreditWarningEnrollCourseGroupDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  degreeCourseGroups,
}) => {
  const { t } = useTranslation('store_course');
  const theme = useTheme();

  return (
    <BaseWarningModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('course_detail.header_banner.purchase.credit.warning_enroll_course_group.title.text')}
      description={
        <Box sx={{ textAlign: 'center' }}>
          {degreeCourseGroups.map((degreeCourseGroup) => {
            const {
              productNames,
              degreeName,
              facultyName,
              majorName,
              urlPdfCourseStructureAndCondition,
            } = degreeCourseGroup;
            return (
              <Box key={degreeName} pb={2}>
                <Typography variant="body1lanna">
                  <b>
                    {t('course_detail.header_banner.purchase.credit.warning_enroll_course_group.course.text')}{' '}
                    <Box component={'span'} sx={{ fontFamily: 'Heavent' }}>
                      &quot;
                    </Box>
                    {productNames.join(', ')}
                    <Box component={'span'} sx={{ fontFamily: 'Heavent' }}>
                      &quot;
                    </Box>
                  </b>
                  {t('course_detail.header_banner.purchase.credit.warning_enroll_course_group.within_degree.text', {
                    degreeName,
                    facultyName,
                    majorName,
                  })}
                </Typography>

                {urlPdfCourseStructureAndCondition ? (
                  <Box>
                    <Link href={urlPdfCourseStructureAndCondition} target="_blank">
                      <Typography variant="body1lanna">
                        {t(
                          'course_detail.header_banner.purchase.credit.warning_enroll_course_group.see_more_detail.button.text',
                        )}
                      </Typography>{' '}
                      <FontAwesomeIcon style={{ fontSize: '16px' }} icon="fa-light fa-arrow-up-right-from-square" />
                    </Link>
                  </Box>
                ) : null}
              </Box>
            );
          })}

          <Typography variant="body1lanna" sx={{ mt: 2, display: 'block' }}>
            {t('course_detail.header_banner.purchase.credit.warning_enroll_course_group.description1.text')}
            <Box sx={{ color: theme.palette.errorV2.main }}>
              {t('course_detail.header_banner.purchase.credit.warning_enroll_course_group.description2.text')}
            </Box>
          </Typography>
        </Box>
      }
      actions={
        <DialogActions
          sx={{
            display: { xs: 'flex', sm: 'grid' },
            gridTemplateColumns: { sm: '1fr 1fr' },
            flexDirection: 'column-reverse',
            gap: { xs: 2, sm: 3 },
          }}
          disableSpacing
        >
          <Button fullWidth variant="outlined" onClick={onClose}>
            {t('course_detail.header_banner.purchase.credit.warning_enroll_course_group.cancel.text')}
          </Button>

          <Button fullWidth variant="contained" disableElevation onClick={onConfirm}>
            {t('course_detail.header_banner.purchase.credit.warning_enroll_course_group.confirm.text')}
          </Button>
        </DialogActions>
      }
    />
  );
};

export default CreditWarningEnrollCourseGroupDialog;
